'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import useSWR, { mutate } from 'swr';
import { toast } from 'sonner';
import { getTeamChannel } from '@/lib/ws-client';

const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes
const HEARTBEAT_INTERVAL_MS = 60 * 1000; // 60 seconds
const MIN_HEARTBEAT_COOLDOWN_MS = 25 * 1000; // Minimum 25 seconds between heartbeats

function getActivityFromPath(pathname?: string | null): string {
  if (!pathname) return 'Active in CRM';
  const clean = pathname.replace(/^\/[a-z]{2}(?=\/)/, '') || pathname;
  if (clean === '/dashboard' || clean === '/') return 'Active in Live Chat';
  if (clean.startsWith('/dashboard/chat')) return 'Chatting with Customer';
  if (clean === '/pipeline' || clean.startsWith('/pipeline')) return 'Viewing Sales Pipeline';
  if (clean === '/orders' || clean.startsWith('/orders')) return 'Managing Orders';
  if (clean === '/contacts' || clean.startsWith('/contacts')) return 'Browsing Contacts';
  if (clean === '/automation' || clean.startsWith('/automation')) return 'Working on Automations';
  if (clean === '/campaigns' || clean.startsWith('/campaigns')) return 'Managing Campaigns';
  if (clean === '/templates' || clean.startsWith('/templates')) return 'Managing Templates';
  if (clean === '/monitoring' || clean.startsWith('/monitoring')) return 'Live Team Monitoring';
  if (clean === '/settings' || clean.startsWith('/settings')) return 'In Settings';
  return 'Active in CRM';
}

export function PresenceTracker() {
  const pathname = usePathname();
  const isSendingRef = useRef(false);
  const lastInteractionRef = useRef(Date.now());
  const lastHeartbeatTimeRef = useRef(0);
  const autoAwayTriggeredRef = useRef(false);
  const currentActivityRef = useRef<string>(getActivityFromPath(pathname));

  useEffect(() => {
    currentActivityRef.current = getActivityFromPath(pathname);
  }, [pathname]);

  useEffect(() => {
    const handleCustomActivity = (e: any) => {
      if (e?.detail && typeof e.detail === 'string') {
        currentActivityRef.current = e.detail;
      }
    };
    window.addEventListener('user-activity-change', handleCustomActivity);
    return () => window.removeEventListener('user-activity-change', handleCustomActivity);
  }, []);

  const { data: user } = useSWR('/api/user', (url) => fetch(url).then((res) => res.json()), {
    dedupingInterval: 10000,
    revalidateOnFocus: false,
  });

  const userRef = useRef(user);
  userRef.current = user;

  const userId = (user as any)?.id;
  const teamId = (user as any)?.teamId || (user as any)?.currentTeamId;

  useEffect(() => {
    if (!userId) return;

    // Record user activity on zeric.link
    const onUserActivity = () => {
      lastInteractionRef.current = Date.now();
      // If user was previously auto-away and starts interacting again, reset flag
      if (autoAwayTriggeredRef.current) {
        autoAwayTriggeredRef.current = false;
      }
    };

    const events = ['mousemove', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((evt) => {
      window.addEventListener(evt, onUserActivity, { passive: true });
    });

    const sendHeartbeat = async (force = false) => {
      if (isSendingRef.current) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

      const now = Date.now();
      // Cooldown protection: prevent rapid bursts
      if (!force && now - lastHeartbeatTimeRef.current < MIN_HEARTBEAT_COOLDOWN_MS) {
        return;
      }

      const currentUser = userRef.current as any;
      const idleTime = now - lastInteractionRef.current;
      const isIdle = idleTime >= INACTIVITY_LIMIT_MS;

      // Auto-away triggered when inactive for 30 minutes
      if (isIdle && !autoAwayTriggeredRef.current && currentUser && currentUser.presenceStatus === 'active') {
        autoAwayTriggeredRef.current = true;
        try {
          await fetch('/api/user/presence', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ autoAway: true }),
          });
          // Update cache locally without triggering an immediate network re-fetch
          mutate(
            '/api/user',
            (prev: any) => (prev ? { ...prev, presenceStatus: 'away' } : prev),
            false
          );
          mutate(
            '/api/user/presence',
            (prev: any) => (prev ? { ...prev, presenceStatus: 'away' } : prev),
            false
          );
          toast.info('You were automatically set to Away due to 30 minutes of inactivity. AI agent is handling your chats.');
        } catch {}
      }

      isSendingRef.current = true;
      lastHeartbeatTimeRef.current = now;

      try {
        const res = await fetch('/api/user/heartbeat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            isUserInteracting: !isIdle,
            isIdle,
            currentActivity: isIdle ? 'Idle' : currentActivityRef.current,
          }),
        });

        if (res.ok) {
          const data = await res.json();
          const latestUser = userRef.current as any;
          if (data && latestUser) {
            // Only update cache if server reported an actual change in presence or custom status
            const hasChanged = Boolean(
              (data.presenceStatus && data.presenceStatus !== latestUser.presenceStatus) ||
              (data.customStatusText !== undefined && data.customStatusText !== (latestUser.customStatusText ?? null)) ||
              (data.customStatusEmoji !== undefined && data.customStatusEmoji !== (latestUser.customStatusEmoji ?? null)) ||
              (data.aiHandleChats !== undefined && data.aiHandleChats !== (latestUser.aiHandleChats ?? false))
            );

            if (hasChanged) {
              mutate(
                '/api/user',
                (prev: any) =>
                  prev
                    ? {
                        ...prev,
                        presenceStatus: data.presenceStatus ?? prev.presenceStatus,
                        customStatusText: data.customStatusText ?? prev.customStatusText,
                        customStatusEmoji: data.customStatusEmoji ?? prev.customStatusEmoji,
                        aiHandleChats: data.aiHandleChats ?? prev.aiHandleChats,
                        statusExpiresAt: data.statusExpiresAt ?? prev.statusExpiresAt,
                      }
                    : prev,
                false
              );

              mutate(
                '/api/user/presence',
                (prev: any) =>
                  prev
                    ? {
                        ...prev,
                        presenceStatus: data.presenceStatus ?? prev.presenceStatus,
                        customStatusText: data.customStatusText ?? prev.customStatusText,
                        customStatusEmoji: data.customStatusEmoji ?? prev.customStatusEmoji,
                        aiHandleChats: data.aiHandleChats ?? prev.aiHandleChats,
                        statusExpiresAt: data.statusExpiresAt ?? prev.statusExpiresAt,
                      }
                    : prev,
                false
              );
            }
          }
        }
      } catch (err) {
        // Silently ignore network dropouts
      } finally {
        isSendingRef.current = false;
      }
    };

    // Immediate initial heartbeat on mount
    sendHeartbeat(true);

    // Ping every 60 seconds
    const interval = setInterval(() => sendHeartbeat(false), HEARTBEAT_INTERVAL_MS);

    // Ping when user returns to tab (respecting cooldown)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        lastInteractionRef.current = Date.now();
        sendHeartbeat(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      events.forEach((evt) => {
        window.removeEventListener(evt, onUserActivity);
      });
    };
  }, [userId]);

  // Listen to team channel for real-time presence changes
  useEffect(() => {
    if (!userId || !teamId) return;

    const channel = getTeamChannel(teamId);
    if (!channel) return;

    const handlePresenceChanged = (payload: any) => {
      if (payload && payload.userId === userId) {
        mutate('/api/user');
        mutate('/api/user/presence');
      }
      mutate('/api/team/members');
      mutate('/api/team/monitoring');
    };

    const handleActivityUpdated = () => {
      mutate('/api/team/monitoring');
      mutate('/api/team/members');
    };

    channel.bind('user-presence-changed', handlePresenceChanged);
    channel.bind('user-activity-updated', handleActivityUpdated);
    return () => {
      channel.unbind('user-presence-changed', handlePresenceChanged);
      channel.unbind('user-activity-updated', handleActivityUpdated);
    };
  }, [userId, teamId]);

  return null;
}
